import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "@arco-design/web-react/es/Trigger/style";
import Trigger from "@arco-design/web-react/es/Trigger";
import { IconLucideMessageSquare, IconLucidePanelTop, IconMore } from '@wegic/icon';
import { Button, Menu } from '@wegic/ui';
import '@wegic/ui/dist/components/Button/index.css';
import '@wegic/ui/dist/components/Menu/index.css';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from '@/const/browser';
import { switchProject } from '@/helpers/project';
import { useHasGeneratedWebsiteArtifact } from '@/helpers/projectStatus';
import { collect } from '@/modules/Collect';
import { canUseAnonymousProjectMode } from '@/modules/CustomRouter/helper';
import { ProjectStatus } from '@/stores/project';
import { useUserStore } from '@/stores/user';
import styles from '../index.module.less';
import RecentProjectPreviewCard from '../RecentProjectPreviewCard';
import { truncateProjectName } from './helper';
const PROJECT_NAME_MAX_LENGTH = 60;
function RecentProjectItem(props) {
    const { activeProjectId, isAnyProjectRenaming, isRenaming, onDeleteClick, onRenameCancel, onRenameSave, onRenameStart, project, variant = 'default' } = props;
    const [menuVisible, setMenuVisible] = useState(false);
    const [projectPreviewVisible, setProjectPreviewVisible] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const renameInputRef = useRef(null);
    const isLogin = useUserStore((state)=>state.isLogin);
    const hasGeneratedWebsiteArtifact = useHasGeneratedWebsiteArtifact();
    const isCollapsedPanel = variant === 'collapsed-panel';
    const isAnonymousMode = canUseAnonymousProjectMode(isLogin);
    const isCollapsedPanelDisabled = isAnyProjectRenaming && !isRenaming && isCollapsedPanel;
    const shouldShowProjectPreview = !isMobile && !isRenaming && !menuVisible;
    const hasGeneratedWebsite = project.status === ProjectStatus.GENERATED || Boolean(project.thumbnailUrl);
    const isActiveProject = project.id === activeProjectId;
    const shouldShowGeneratedWebsiteIcon = hasGeneratedWebsite || isActiveProject && hasGeneratedWebsiteArtifact;
    useEffect(()=>{
        if (!isRenaming) {
            setRenameValue('');
            return;
        }
        requestAnimationFrame(()=>{
            var _renameInputRef_current;
            (_renameInputRef_current = renameInputRef.current) === null || _renameInputRef_current === void 0 ? void 0 : _renameInputRef_current.focus();
        });
    }, [
        isRenaming
    ]);
    const stopProjectSwitch = (event)=>{
        event.stopPropagation();
    };
    const handleMoreClick = (event)=>{
        event.stopPropagation();
    };
    const handleRenameClick = ()=>{
        setMenuVisible(false);
        onRenameStart(project.id);
    };
    const handleDeleteClick = ()=>{
        setMenuVisible(false);
        onDeleteClick(project);
    };
    const handleProjectClick = ()=>{
        if (isRenaming || project.id === activeProjectId) {
            return;
        }
        collect('open_project', {
            object: 'sidebar'
        });
        switchProject(project.id);
    };
    const handleProjectKeyDown = (event)=>{
        if (isRenaming || project.id === activeProjectId || event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        event.preventDefault();
        collect('open_project', {
            object: 'sidebar'
        });
        switchProject(project.id);
    };
    const handleRenameChange = (event)=>{
        setRenameValue(event.target.value);
    };
    const submitRename = ()=>{
        const nextName = truncateProjectName(renameValue.trim(), PROJECT_NAME_MAX_LENGTH);
        if (!nextName) {
            onRenameCancel();
            return;
        }
        onRenameSave(project, nextName);
    };
    const handleRenameKeyDown = (event)=>{
        if (event.key === 'Enter') {
            event.preventDefault();
            submitRename();
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            onRenameCancel();
        }
    };
    return /*#__PURE__*/ _jsx("li", {
        className: classNames(styles['project-list-item'], {
            [styles['project-list-item-collapsed-panel']]: isCollapsedPanel,
            [styles['project-list-item-collapsed-panel-disabled']]: isCollapsedPanelDisabled,
            [styles['project-list-item-dimmed']]: isAnyProjectRenaming && !isRenaming && !isCollapsedPanel,
            [styles['project-list-item-renaming']]: isRenaming
        }),
        children: /*#__PURE__*/ _jsx(Trigger, {
            trigger: "hover",
            position: "right",
            mouseEnterDelay: 200,
            popupAlign: {
                right: 8
            },
            popupVisible: shouldShowProjectPreview && projectPreviewVisible,
            onVisibleChange: setProjectPreviewVisible,
            popup: ()=>/*#__PURE__*/ _jsx(RecentProjectPreviewCard, {
                    project: project
                }),
            updateOnScroll: true,
            children: /*#__PURE__*/ _jsxs("div", {
                className: classNames(styles.item, styles['project-item'], {
                    [styles.active]: isActiveProject,
                    [styles['project-item-collapsed-panel']]: isCollapsedPanel,
                    [styles['project-item-renaming']]: isRenaming && !isCollapsedPanel,
                    [styles['project-item-collapsed-panel-renaming']]: isRenaming && isCollapsedPanel
                }),
                role: "button",
                tabIndex: isRenaming ? -1 : 0,
                title: project.name,
                onClick: handleProjectClick,
                onKeyDown: handleProjectKeyDown,
                children: [
                    !isCollapsedPanel && (shouldShowGeneratedWebsiteIcon ? /*#__PURE__*/ _jsx(IconLucidePanelTop, {
                        className: styles.icon,
                        fontSize: 16
                    }) : /*#__PURE__*/ _jsx(IconLucideMessageSquare, {
                        className: styles.icon,
                        fontSize: 16
                    })),
                    isRenaming ? /*#__PURE__*/ _jsx("input", {
                        ref: renameInputRef,
                        className: styles['project-rename-input'],
                        value: renameValue,
                        placeholder: project.name,
                        onBlur: submitRename,
                        onChange: handleRenameChange,
                        onClick: stopProjectSwitch,
                        onKeyDown: handleRenameKeyDown,
                        onMouseDown: stopProjectSwitch,
                        onPointerDown: stopProjectSwitch
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: styles.label,
                        children: project.name
                    }),
                    !isAnonymousMode && !isRenaming && /*#__PURE__*/ _jsx(Trigger, {
                        trigger: "hover",
                        position: "br",
                        classNames: "slideFromTop",
                        popupAlign: {
                            bottom: [
                                0,
                                4
                            ]
                        },
                        popupVisible: menuVisible,
                        boundaryDistance: {
                            bottom: 125
                        },
                        onVisibleChange: setMenuVisible,
                        popup: ()=>/*#__PURE__*/ _jsx("div", {
                                className: styles['project-actions-menu'],
                                onClick: stopProjectSwitch,
                                onMouseDown: stopProjectSwitch,
                                onPointerDown: stopProjectSwitch,
                                children: /*#__PURE__*/ _jsx(Menu, {
                                    menuItems: [
                                        {
                                            type: 'item',
                                            key: 'rename',
                                            text: intl.get('common.action.rename'),
                                            onClick: handleRenameClick
                                        },
                                        {
                                            type: 'item',
                                            key: 'delete',
                                            text: intl.get('common.action.delete'),
                                            status: 'danger',
                                            onClick: handleDeleteClick
                                        }
                                    ]
                                })
                            }),
                        updateOnScroll: true,
                        children: /*#__PURE__*/ _jsx(Button, {
                            className: classNames(styles['project-action-button'], {
                                [styles['project-action-button-active']]: menuVisible
                            }),
                            size: "mini",
                            type: "secondary",
                            icon: /*#__PURE__*/ _jsx(IconMore, {
                                fontSize: 16
                            }),
                            onClick: handleMoreClick,
                            onMouseDown: stopProjectSwitch,
                            onPointerDown: stopProjectSwitch
                        })
                    })
                ]
            })
        })
    });
}
export default RecentProjectItem;
